import { ResizableBox } from 'react-resizable';
import styled from '@emotion/styled';
import css from '@emotion/css';
import './editor.css';
import { EditorContent } from '@tiptap/react';
import { colorsPrimary, grayScale } from 'app/colors';
import { truncateCss } from 'app/typography';
import ColumnResizeIcon from 'components/Kizen/Icon/custom/ColumnResizeIcon';

export const HorizontalRuleStyle = css`
  hr {
    box-sizing: border-box;
    border-width: 0;
    border-style: solid;
    border-color: inherit;
    margin: 0;
    height: 0;
    color: ${grayScale.mediumDark};
    border-top-width: 1px;
  }
`;

const proseMirrorStyle = ({
  defaultFontFamily = 'Arial',
  defaultFontSize = '14px',
} = {}) => css`
  .ProseMirror {
    padding: 10px;
    outline: none;
    word-break: break-word;

    /* Reccomended layout CSS */
    > * + * {
      margin-top: 0;
    }

    ${HorizontalRuleStyle}

    /* Placeholder (at the top) */
    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: ${grayScale.medium};
      pointer-events: none;
      height: 0;
    }

    p * {
      line-height: inherit;
    }

    /* Set default font-family for now */
    font-family: ${defaultFontFamily};
    font-size: ${defaultFontSize};

    /*
    We need to make sure we inherit the font info, otherwise a
    nested <u> or <strong> will revert back the global settings we have
    */
    * {
      font-family: inherit;
      font-size: inherit;
      line-height: 1.5em;
    }

    &&& .mention {
      background: ${grayScale.white};
      position: relative;
      max-width: 250px;
      height: 24px;
      padding-left: 0px;
      padding-right: 0px;
      :focus {
        outline: none;
      }
      bottom: -2px;
      ${truncateCss}
    }
  }
`;

export const StyledResizableBox = styled(ResizableBox)`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const ResizeIcon = styled(ColumnResizeIcon)`
  position: absolute;
  left: 50%;
  bottom: -16px;
  transform: rotate(90deg);
  cursor: ns-resize;

  &&,
  && svg {
    width: 3px;
    height: 30px;
  }
`;

export const BottomBarWrapper = styled.div`
  border-top: 1px solid ${grayScale.medium};
`;

export const StyledEditorContent = styled(EditorContent)`
  flex: 1;
  height: 100%;
  overflow-y: auto;

  ${({ disabled = false }) =>
    disabled &&
    css`
      background-color: ${grayScale.light};
    `}

  ${({ blurContent }) =>
    blurContent &&
    css`
      filter: blur(2px);
    `}

  ${({ enableResize, initialHeight = 200 }) =>
    !enableResize &&
    css`
      height: ${initialHeight}px;
    `}
`;

const codeBlockStyle = ({ codeBlockColors }) => css`
  .ProseMirror > pre > code {
    font-family: 'Courier New', Monospace;
    font-size: 14px;
  }
  /* taken from highlight.js/styles/github.css */
  .hljs {
    color: #24292e;
    background: #ffffff;
  }
  .hljs-doctag,
  .hljs-keyword,
  .hljs-meta .hljs-keyword,
  .hljs-template-tag,
  .hljs-template-variable,
  f .hljs-type,
  .hljs-variable.language_ {
    /* prettylights-syntax-keyword */
    color: #d73a49;
  }
  .hljs-title,
  .hljs-title.class_,
  .hljs-title.class_.inherited__,
  .hljs-title.function_ {
    /* prettylights-syntax-entity */
    color: #6f42c1;
  }
  .hljs-attr,
  .hljs-attribute,
  .hljs-literal,
  .hljs-meta,
  .hljs-number,
  .hljs-operator,
  .hljs-variable,
  .hljs-selector-attr,
  .hljs-selector-class,
  .hljs-selector-id {
    /* prettylights-syntax-constant */
    color: #005cc5;
  }
  .hljs-regexp,
  .hljs-string,
  .hljs-meta .hljs-string {
    /* prettylights-syntax-string */
    color: #032f62;
  }
  .hljs-built_in,
  .hljs-symbol {
    /* prettylights-syntax-variable */
    color: #e36209;
  }
  .hljs-comment,
  .hljs-code,
  .hljs-formula {
    /* prettylights-syntax-comment */
    color: #6a737d;
  }
  .hljs-name,
  .hljs-quote,
  .hljs-selector-tag,
  .hljs-selector-pseudo {
    /* prettylights-syntax-entity-tag */
    color: #22863a;
  }
  .hljs-subst {
    /* prettylights-syntax-storage-modifier-import */
    color: #24292e;
  }
  .hljs-section {
    /* prettylights-syntax-markup-heading */
    color: #005cc5;
    font-weight: bold;
  }
  .hljs-bullet {
    /* prettylights-syntax-markup-list */
    color: #735c0f;
  }
  .hljs-emphasis {
    /* prettylights-syntax-markup-italic */
    color: #24292e;
    font-style: italic;
  }
  .hljs-strong {
    /* prettylights-syntax-markup-bold */
    color: #24292e;
    font-weight: bold;
  }
  .hljs-addition {
    /* prettylights-syntax-markup-inserted */
    color: #22863a;
    background-color: #f0fff4;
  }
  .hljs-deletion {
    /* prettylights-syntax-markup-deleted */
    color: #b31d28;
    background-color: #ffeef0;
  }
  .hljs-char.escape_,
  .hljs-link,
  .hljs-params,
  .hljs-property,
  .hljs-punctuation,
  .hljs-tag {
    /* purposely ignored */
  }
  /* Custom colors */
  ${Object.entries(codeBlockColors || {}).reduce((acc, [key, value]) => {
    return (
      acc +
      `
        .hljs-${key} {
          color: ${value};
        }
      `
    );
  }, '')}
`;

export const EditorWrapper = styled.div`
  ${proseMirrorStyle}
  ${codeBlockStyle}
  border: 1px solid ${grayScale.medium};
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
  background: white;

  ${({ hasLoadingState }) =>
    hasLoadingState &&
    css`
      position: relative;
      container-type: inline-size;
    `}

  .ProseMirror {
    height: 100%;

    ${({ fitToContent }) =>
      !fitToContent &&
      css`
        overflow: auto;
      `}

    ${({ isLoading }) =>
      isLoading &&
      css`
        min-height: 120px;
      `}
  }
`;

export const FloatingEditorWrapper = styled.div`
  ${proseMirrorStyle}
  ${codeBlockStyle}

  ${({ hasLoadingState }) =>
    hasLoadingState &&
    css`
      position: relative;
      container-type: inline-size;
    `}

  .ProseMirror {
    background: transparent;
    border-radius: 0;
    ${({ showBorder }) =>
      showBorder
        ? css`
            border: 1px solid ${colorsPrimary.blue.dark};
          `
        : css`
            border: none;
          `}

    ${({ isLoading }) =>
      isLoading &&
      css`
        min-height: 120px;
      `}
  }
`;

export const LoadingContainer = styled.div`
  position: absolute;
  top: 20px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;
